import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Lock } from "lucide-react";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import BenefitRow from "src/components/app/program/BenefitRow";
import MilestoneDocumentsPrompt from "src/components/app/program/MilestoneDocumentsPrompt";
import analytics from "src/helpers/analytics";
import { SCREEN_NAMES } from "src/helpers/analytics/events";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getProgramDetailsGfcRequest } from "src/redux/slices/programSlice";
import {
  isBenefitAvailed,
  isMilestoneLocked,
  needsMilestoneDocuments,
  parsePeriod,
} from "src/constants/milestones";
import { useBenefitAction } from "src/hooks/useBenefitAction";
import { formatDateToLocaleString } from "src/helpers/common";
import {
  Milestone,
  MilestoneBenefit,
  MilestoneBenefitAction,
} from "src/types/program.types";
import Loader from "src/components/common/Loader";

const RING_RADIUS = 42;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const JourneyProgress: React.FC<{
  milestones: Milestone[];
  currentMilestoneOrder: number;
  startDate?: string;
  endDate?: string;
}> = ({ milestones, currentMilestoneOrder, startDate, endDate }) => {
  const total = milestones.length;
  // currentMilestoneOrder reflects how far the user is in the program
  // timeline (elapsed months), independent of whether they've clicked
  // "claim" on every benefit along the way — don't derive this from
  // benefit.touched, most users won't have claimed everything they're
  // eligible for.
  const completed = Math.min(Math.max(currentMilestoneOrder, 0), total);
  const percent = total === 0 ? 0 : completed / total;
  const dashOffset = RING_CIRCUMFERENCE * (1 - percent);

  if (total === 0) return null;

  const subtitle =
    completed === total
      ? "You've completed your journey! \u{1F389}"
      : `${completed} of ${total} milestones done · ${total - completed} to go`;

  return (
    <div
      className="rounded-2xl p-5 mb-5 flex items-center gap-4"
      style={{
        background:
          "linear-gradient(135deg, rgba(var(--app-primary-rgb), 0.12), rgba(var(--app-secondary-rgb), 0.12))",
        border: "1px solid rgba(var(--app-primary-rgb), 0.15)",
      }}
    >
      <div className="relative w-24 h-24 shrink-0">
        <svg viewBox="0 0 96 96" className="w-24 h-24 -rotate-90">
          <defs>
            <linearGradient
              id="journey-ring-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" style={{ stopColor: "var(--app-primary)" }} />
              <stop
                offset="100%"
                style={{ stopColor: "var(--app-secondary)" }}
              />
            </linearGradient>
          </defs>
          <circle
            cx="48"
            cy="48"
            r={RING_RADIUS}
            fill="none"
            stroke="rgba(var(--app-primary-rgb), 0.15)"
            strokeWidth="8"
          />
          <circle
            cx="48"
            cy="48"
            r={RING_RADIUS}
            fill="none"
            stroke="url(#journey-ring-gradient)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className="text-lg font-bold"
            style={{ color: "var(--app-primary)" }}
          >
            {completed}/{total}
          </span>
        </div>
      </div>
      <div>
        <h2
          className="text-base font-semibold"
          style={{ color: "var(--card-title-color)" }}
        >
          Your Journey
        </h2>
        <p
          className="text-sm mt-0.5"
          style={{ color: "var(--card-desc-color)" }}
        >
          {subtitle}
        </p>
        {startDate && endDate && (
          <p
            className="text-xs mt-1"
            style={{ color: "var(--text-secondary)" }}
          >
            {formatDateToLocaleString(startDate)} –{" "}
            {formatDateToLocaleString(endDate)}
          </p>
        )}
      </div>
    </div>
  );
};

const ProgramDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const dispatch = useAppDispatch();
  const { programDetails } = useAppSelector((state) => state.program);

  const milestones = programDetails.data?.milestones ?? [];
  const currentMilestoneOrder = programDetails.data?.currentMilestoneOrder ?? 0;
  const { startDate, endDate } = programDetails.data ?? {};

  useEffect(() => {
    // Event: PROGRAM_DETAILS_SCREEN_VIEWED
    analytics.pageView(SCREEN_NAMES.PROGRAM_DETAILS_SCREEN);
  }, []);

  useEffect(() => {
    new Promise((resolve, reject) => {
      dispatch(
        getProgramDetailsGfcRequest({
          resolve,
          reject,
        }),
      );
    }).catch((error) => {
      console.error("Error while fetching GFC program details", error);
    });
  }, [dispatch]);

  const handleBenefitAction = useBenefitAction();

  const handleMilestoneDocuments = (milestone: Milestone) => {
    navigate(
      `/app/${supportProgramKey}/program-details/milestone-documents/${milestone.key}`,
    );
  };

  const handleBackClick = () => {
    // navigate(-1);
    navigate(`/app/${supportProgramKey}/home-screen`);
  };

  return (
    <Layout
      supportProgramKey={supportProgramKey || ""}
      header={
        <Header
          title="Program Details"
          titlePosition="center"
          showBackButton={true}
          onBackClick={handleBackClick}
        />
      }
      stickyHeader={true}
      showBottomBar={false}
    >
      <div className="p-4 h-full">
        {programDetails.isLoading ? (
          <div className="flex flex-col items-center justify-center gap-2 h-full">
            <Loader />
            <p className="text-sm text-gray-500">Fetching Program Details...</p>
          </div>
        ) : programDetails.error ? (
          <p className="text-sm text-gray-500">Something went wrong!</p>
        ) : milestones.length === 0 ? (
          <p className="text-sm text-gray-500">No milestones found.</p>
        ) : (
          <>
            <JourneyProgress
              milestones={milestones}
              currentMilestoneOrder={currentMilestoneOrder}
              startDate={startDate}
              endDate={endDate}
            />
            <div className="flex flex-col">
              {milestones.map((milestone, index) => {
                const isLast = index === milestones.length - 1;
                const isLocked = isMilestoneLocked(milestone);
                const allBenefitsAvailed = milestone.benefits.every((benefit) =>
                  isBenefitAvailed(milestone, benefit),
                );

                const chipColor = milestone.isActive
                  ? "var(--app-primary)"
                  : isLocked
                    ? "var(--text-secondary)"
                    : allBenefitsAvailed
                      ? "var(--status-success)"
                      : "var(--text-secondary)";
                const lineColor = isLocked
                  ? "var(--card-border)"
                  : "var(--status-success)";

                const { label: periodLabel, value: periodValue } = parsePeriod(
                  milestone.period,
                );

                return (
                  <div key={milestone.key} className="flex gap-3">
                    {/* Left rail: calendar-style period badge + connecting line */}
                    <div className="flex flex-col items-center shrink-0 pt-1">
                      <div
                        className="w-10 h-10 rounded-lg overflow-hidden shrink-0 shadow-md flex flex-col"
                        style={{
                          border: `1px solid ${chipColor}`,
                        }}
                      >
                        <div
                          className="h-3.5 flex items-center justify-center text-[10px] font-bold uppercase tracking-wide text-white"
                          style={{ backgroundColor: chipColor }}
                        >
                          {periodLabel}
                        </div>
                        <div
                          className="flex-1 flex items-center justify-center text-sm font-bold"
                          style={{
                            backgroundColor: "var(--card-background)",
                            color: chipColor,
                          }}
                        >
                          {periodValue}
                        </div>
                      </div>
                      {!isLast && (
                        <span
                          className="w-0.5 flex-1 mt-1"
                          style={{ backgroundColor: lineColor }}
                        />
                      )}
                    </div>

                    {/* Right: the whole milestone as one card, benefits as rows inside it */}
                    <div className="flex-1 min-w-0 mb-4">
                      <MilestoneCard
                        milestone={milestone}
                        periodLabel={periodLabel}
                        periodValue={periodValue}
                        onBenefitAction={(benefit, action) =>
                          handleBenefitAction(milestone, benefit, action)
                        }
                        onDocumentsAction={() =>
                          handleMilestoneDocuments(milestone)
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

// The whole milestone renders as one card: a header with the benefit count and
// the milestone's state, one row per benefit, and — when the milestone is gated
// behind documents — an upload footer. Grouping keeps a multi-benefit month
// (M2 has two) reading as a single unit instead of a stack of look-alike cards.
const MilestoneCard: React.FC<{
  milestone: Milestone;
  periodLabel: string;
  periodValue: string;
  onBenefitAction: (
    benefit: MilestoneBenefit,
    action: MilestoneBenefitAction,
  ) => void;
  onDocumentsAction: () => void;
}> = ({
  milestone,
  periodLabel,
  periodValue,
  onBenefitAction,
  onDocumentsAction,
}) => {
  const isLocked = isMilestoneLocked(milestone);
  const docsRequired = needsMilestoneDocuments(milestone);
  // Locked or awaiting documents — either way nothing inside can be claimed
  // yet, so the content is dimmed and carries a lock.
  const isGated = isLocked || docsRequired;
  const allBenefitsAvailed = milestone.benefits.every((benefit) =>
    isBenefitAvailed(milestone, benefit),
  );
  const isClaimable = !isGated && !allBenefitsAvailed;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: "var(--card-background)",
        border: isClaimable
          ? "1px solid rgba(var(--app-primary-rgb), 0.25)"
          : "1px solid var(--card-border)",
        borderLeft: isClaimable ? "4px solid var(--app-primary)" : undefined,
        boxShadow: isClaimable
          ? "0 6px 20px rgba(var(--app-primary-rgb), 0.18)"
          : undefined,
      }}
    >
      {/* The period already sits in the rail chip on the left, so the header
          only carries the milestone's state. */}
      <div className="flex items-center justify-between gap-2 px-3 pt-3">
        <h3
          className="text-sm font-semibold min-w-0"
          style={{
            color: isGated
              ? "var(--disabled-text-color)"
              : "var(--card-title-color)",
          }}
        >
          Benefits this month
        </h3>

        {/* Availed is shown per benefit, not for the milestone as a whole */}
        {isGated && (
          <div
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "var(--card-border)" }}
          >
            <Lock
              className="w-3.5 h-3.5"
              style={{ color: "var(--disabled-text-color)" }}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 p-3">
        {milestone.benefits.map((benefit) => (
          <BenefitRow
            key={benefit.name || benefit.title}
            milestone={milestone}
            benefit={benefit}
            onAction={(action) => onBenefitAction(benefit, action)}
          />
        ))}
      </div>

      {isLocked && (
        <p
          className="px-3 pb-3 -mt-1 text-xs font-medium"
          style={{ color: "var(--disabled-text-color)" }}
        >
          This will be activated in {periodLabel} {periodValue}
        </p>
      )}

      {docsRequired && (
        <MilestoneDocumentsPrompt
          milestone={milestone}
          onAction={onDocumentsAction}
        />
      )}
    </div>
  );
};

export default ProgramDetailsScreen;
